import type { PossibleTypesMap, TypePolicies } from "@apollo/client";

/**
 * Fragment matching and type policies of the Apollo client.
 *
 * Kept out of `client.ts` so tests can reach the real policies: that module is
 * globally mocked, so anything importing it in a test gets the mock instead.
 */
export const possibleTypes: PossibleTypesMap = {
  ObjectSizeID: ["TableID", "ContAggID"],
};

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      // `ConnectorsQuerier` carries no id, so it is stored inline on ROOT_QUERY
      // and the default policy *replaces* it on every write. Two queries
      // selecting different fields under `connectors` then evict each other,
      // and each eviction leaves the other's cache diff incomplete, which makes
      // it refetch — an endless alternating refetch storm.
      connectors: { merge: true },
      activityLog: {
        keyArgs: ["projectId", "serviceId"],
        // Pages are addressed by the `end` cursor. A request without one is
        // the first page and replaces what is cached for that filter; every
        // other page appends, so `fetchMore` needs no `updateQuery`.
        merge(existing, incoming, { args }) {
          if (!args?.end || !existing) return incoming;
          return {
            ...incoming,
            items: [...existing.items, ...incoming.items],
          };
        },
      },
      getRecommendations: {
        // merge: true, // Throws a new error.
        merge(_existing, incoming) {
          return incoming;
        },
      },
      getProject: { merge: true },
    },
  },
  // `orbProducts(projectId:)` and `orbAllProducts` return the same product ids
  // with different plan sets, since plans are per region and projects and tiers
  // reach different ones. Normalizing `Product` by its `id` collapses those
  // scopes onto one entity, so the last write wins for all of them.
  Product: { keyFields: false },
  Project: {
    fields: {
      // `EntitlementSpec` has no id. GetProject selects the full object;
      // GetBillingAccountCorporateInvoicing selects only `plan_type` —
      // without a merge function the smaller write wipes out the rest.
      entitlementSpec: { merge: true },
    },
  },
  PeeringConnection: { keyFields: false },
  PeerVPC: { keyFields: ["id", "cidrBlocks"] },
};
